import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, Store, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { submitPartnerEnquiry } from "@/lib/partner-enquiry.functions";

export const Route = createFileRoute("/partner-enquiry")({
  component: PartnerEnquiryPage,
  head: () => ({
    meta: [
      { title: "Bulk enquiry & channel partners. TruWe" },
      {
        name: "description",
        content:
          "Order TruWe in bulk for your store, clinic or gym, or register as a TruWe channel partner and distributor in your city.",
      },
      { property: "og:title", content: "Bulk enquiry & channel partners. TruWe" },
      {
        property: "og:description",
        content: "Register your store or distribution channel to stock TruWe formulas.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://truwe.in/partner-enquiry" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/partner-enquiry" }],
  }),
});

const inputCls =
  "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

function PartnerEnquiryPage() {
  const submit = useServerFn(submitPartnerEnquiry);
  const [enquiryType, setEnquiryType] = useState<"bulk" | "channel_partner">("bulk");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    storeName: "",
    channelName: "",
    location: "",
    monthlyVolume: "",
    message: "",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name");
    if (form.location.trim().length < 2) return toast.error("Please enter your city / location");
    setLoading(true);
    try {
      const res = await submit({ data: { ...form, enquiryType } });
      if (!res.ok) {
        toast.error(
          res.error === "rate_limited"
            ? "You've already sent a few enquiries. We'll be in touch soon."
            : res.error === "invalid_phone"
              ? "Please enter a valid mobile number"
              : "Something went wrong. Please try again.",
        );
        return;
      }
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center md:px-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand/10 text-brand">
          <Check className="h-7 w-7" />
        </span>
        <h1 className="mt-5 font-display text-3xl font-bold text-brand-deep">
          Enquiry received.
        </h1>
        <p className="mt-3 text-muted-foreground">
          Thank you, {form.name.split(" ")[0]}. Our partnerships team will call you on{" "}
          {form.phone} within one business day.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 md:px-8 md:py-20">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
        Partnerships
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
        Bulk orders & channel partners.
      </h1>
      <p className="mt-3 text-muted-foreground">
        Stocking TruWe for a store, clinic, gym or pharmacy chain, or want to distribute in your
        region? Register below and our partnerships team will get in touch.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              ["bulk", "Bulk enquiry", "One-off or repeat bulk orders", Boxes],
              ["channel_partner", "Channel partner", "Distribute / retail TruWe", Store],
            ] as const
          ).map(([value, title, sub, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setEnquiryType(value)}
              aria-pressed={enquiryType === value}
              className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                enquiryType === value
                  ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                  : "border-border bg-card hover:border-brand/40"
              }`}
            >
              <Icon className="mt-0.5 h-5 w-5 flex-none text-brand" />
              <span>
                <span className="block font-display font-semibold text-brand-deep">{title}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
            </button>
          ))}
        </div>

        <Field label="Full name">
          <input className={inputCls} value={form.name} onChange={set("name")} required maxLength={120} autoComplete="name" placeholder="Your name" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Contact number">
            <input className={inputCls} value={form.phone} onChange={set("phone")} required type="tel" inputMode="tel" maxLength={20} autoComplete="tel" placeholder="10-digit mobile" />
          </Field>
          <Field label="Email (optional)">
            <input className={inputCls} value={form.email} onChange={set("email")} type="email" maxLength={320} autoComplete="email" placeholder="you@business.com" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Store / business name">
            <input className={inputCls} value={form.storeName} onChange={set("storeName")} maxLength={160} placeholder="e.g. Sharma Medicos" />
          </Field>
          <Field label="Channel name" hint="Pharmacy, gym, clinic, e-commerce, distributor…">
            <input className={inputCls} value={form.channelName} onChange={set("channelName")} maxLength={160} placeholder="e.g. Pharmacy chain" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Location">
            <input className={inputCls} value={form.location} onChange={set("location")} required maxLength={200} placeholder="City, state" />
          </Field>
          <Field label="Expected monthly volume (optional)">
            <input className={inputCls} value={form.monthlyVolume} onChange={set("monthlyVolume")} maxLength={80} placeholder="e.g. 100-200 units" />
          </Field>
        </div>
        <Field label="Anything else (optional)">
          <textarea className={`${inputCls} min-h-[110px]`} value={form.message} onChange={set("message")} maxLength={2000} placeholder="Tell us about your business" />
        </Field>

        <Button type="submit" disabled={loading} size="lg" className="w-full rounded-full">
          {loading ? "Sending…" : "Submit enquiry"}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">
          We'll only use these details to contact you about this enquiry.
        </p>
      </form>
    </div>
  );
}
